import { Images } from '@/assets/images';
import { DocTypeEnum, REGEX } from '@/constant';
import { cls, cn, dayjs, diffDate, formatDate } from '@/utils';

import { range } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { CheckboxType, FormConfig } from '../define-document-write.type';

const initializeValue = {
	// Page 1
	registryDate: '',
	district: '',
	districtName: '',

	franchiseName: '',
	franchiseBusinessNumber: '',
	franchiseRegistrationNo: '',
	franchisePhone: '',
	franchiseAddress: '',

	workAddress: '',
	addressOfResidence: '',
	product: '',
	cultivatedOther: '',
	cultivatedArea: '',
	jobDescription: '',
	requiredManpower: '',
	preferredJoiningMethod: '',
	workerRecruitmentType: '',
	newCount: '',
	rehiredCount: '',
	preferredAgeMin: '',
	preferredAgeMax: '',
	preferredNationalityPriority: '',
	preferredNationalityPrioritySecond: '',
	expectedEmploymentPeriod: '',
	totalEmploymentMonths: '',
	employmentStartDate: '',
	employmentEndDate: '',
	month1: '',
	month2: '',
	month3: '',
	month4: '',
	month5: '',
	month6: '',
	month7: '',
	month8: '',
	month9: '',
	month10: '',
	month11: '',
	month12: '',
	currentYear: '',
	currentRegion: '',
	formSubmissionDate: '',
	registrantName: '',
	registrantSignature: '',

	// Page 2
	workingStartDay: '',
	workingEndDay: '',
	workingStartTime: '',
	workingEndTime: '',
	totalMonthlyWorkingHours: '',
	flexibleHoursLimit: '',

	breakCountPerDay: '1',
	breakHours: '1',
	breakMinutes: '',

	weeklyRestDays: '1',
	restDaysOther: '',

	wageAmount: '',
	wageAndPaymentMethod: '',

	isBonusPaid: '',
	bonusAmount: '',
	allowanceAmount: '',
	overtimeHourlyWage: '',

	salaryPeriodStartDate: '',
	salaryPeriodEndDate: '',
	isPaidInCurrentMonth: '',
	paymentDayOfMonth: '',
	isPaidInCurrentWeek: '',
	paymentDayOfWeek: '',

	isAccommodationProvided: '0',
	accommodationType: '',
	isAccommodationPaidByWorker: '0',
	accommodationWorkerFeeAmount: '323000',

	isMealsProvided: '0',
	individualMeals: ['1'] as string[],
	mealsWorkerFeeAmount: '107000',
	isMealsFreeForWorker: '',
	other: '',

	employerFactCheckSection: [] as string[],
	cooperateDataSubmission: '',

	foreignWorkersRegisteringFormJson: range(0, 5).map((item) => ({
		id: item.toString(),
		priorityOrder: '',
		fullName: '',
		nationality: '',
		alienRegistrationNumber: '',
		passportNumber: '',
		totalContractDays: '',
		actualWorkingDays: '',
		is75PercentContractFulfilled: '',
	})),
	remarks: '',
};

const preferredJoiningMethodOption = ['0', '1', '2', '3'];
const weeklyRestDaysOption = ['0', '1', '2'];
const paymentTypeOption = ['0', '1', '2', '3'];
const bonusAndAllowancesOption = ['0', '1'];
// 조식 / 중식 / 석식 — multi-select (can pick several meals)
const individualMealsOption = ['0', '1', '2'];

type IValues = typeof initializeValue;

export const RECRUITING_FOREIGN_WORKER_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ARF_14
> = {
	initializeValue,
	// Khi chọn accommodationType hoặc isAccommodationPaidByWorker thì
	// tự động check isAccommodationProvided (options ['0'] -> checked = '0').
	derivedFields: [
		// 고용 예정 기간: 시작/종료일 선택 시 총 개월 수 자동 계산
		{
			when: ['employmentStartDate', 'employmentEndDate'],
			set: 'totalEmploymentMonths',
			compute: (data) =>
				data.employmentStartDate && data.employmentEndDate
					? String(
							diffDate(
								data.employmentEndDate,
								data.employmentStartDate,
								'month'
							)
						)
					: '',
		},
		{
			when: ['accommodationType', 'isAccommodationPaidByWorker'],
			set: 'isAccommodationProvided',
			compute: (data) =>
				isNotNilOrEmpty(data.accommodationType) ||
				isNotNilOrEmpty(data.isAccommodationPaidByWorker)
					? '0'
					: data.isAccommodationProvided,
		},
		{
			when: ['individualMeals', 'isMealsFreeForWorker'],
			set: 'isMealsProvided',
			compute: (data) =>
				isNotNilOrEmpty(data.individualMeals) ||
				isNotNilOrEmpty(data.isMealsFreeForWorker)
					? '0'
					: data.isMealsProvided,
		},
	],
	title: '외국인 계절근로자 고용 신청서',
	mapPropToValues: ({ detailData, franchiseData, applicantLodgingData }) => {
		const { unitInfo } = detailData || {};
		const today = formatDate(dayjs());
		const businessAddress = franchiseData?.address || '';
		const district = unitInfo?.countyDistrict || '';
		const employerName = franchiseData?.employerName || '';
		const createForeignWorkersRegistering = (index: number) => ({
			id: index.toString(),
			priorityOrder: '',
			fullName: '',
			nationality: '',
			alienRegistrationNumber: '',
			passportNumber: '',
			totalContractDays: '',
			actualWorkingDays: '',
			is75PercentContractFulfilled: '',
		});
		return {
			// Title
			registryDate: today,
			district,
			districtName: district,

			// 신청자 현황 (Applicant status) — from franchise detail (FR1-03)
			franchiseName: employerName,
			franchiseBusinessNumber: franchiseData?.businessRegistNo || '',
			franchiseRegistrationNo: isNotNilOrEmpty(franchiseData?.employerBirthDate)
				? `${formatDate(franchiseData?.employerBirthDate, {
						outputFormat: 'YYMMDD',
					})}-`
				: '',
			franchisePhone: franchiseData?.employerPhone || '',
			franchiseAddress: businessAddress,

			// 구인 사항 (Recruitment details)
			workAddress: businessAddress,
			addressOfResidence: applicantLodgingData?.lodgingAddress || '',
			product: franchiseData?.productionTypeDetail || '',
			cultivatedArea: franchiseData?.productionScale || '',

			// Footer
			currentYear: today,
			currentRegion: district,
			formSubmissionDate: today,
			registrantName: employerName.slice(0, 5),

			// Page 2 — 1-1. 근로시간 (from FR1-03, else default 08:00~17:00, 209h/month)
			workingStartTime: franchiseData?.workStartTime || '08:00',
			workingEndTime: franchiseData?.workEndTime || '17:00',
			totalMonthlyWorkingHours: '209',
			// 1-2. 휴게시간 (from FR1-03, else default 1일 1회 1시간)
			breakCountPerDay: franchiseData?.dailyBreaksNumber
				? String(franchiseData.dailyBreaksNumber)
				: '1',
			breakHours: franchiseData?.dailyBreaksHour
				? String(franchiseData.dailyBreaksHour)
				: '1',
			breakMinutes: franchiseData?.dailyBreaksMin
				? String(franchiseData.dailyBreaksMin)
				: '',
			// 1-4. 임금 default ([O] 월급 2,156,880원, 연장근로 시간당 10,320원)
			wageAndPaymentMethod: '0',
			wageAmount: '2156880',
			overtimeHourlyWage: '10320',
			foreignWorkersRegisteringFormJson: range(0, 5).map(
				createForeignWorkersRegistering
			),
		};
	},
	pages: [
		{
			background: {
				src: Images.formForRecruitingForeignWorkers,
				className: 'template',
				dataSource: Images.formForRecruitingForeignWorkers,
			},
			textAreas: [
				// 신청자 현황 (Applicant status)
				(values) => ({
					name: 'registryDate',
					maxHeight: 34,
					className: 'text-[16px] text-center',
					containerClassName: cls(
						'absolute top-[95px] left-[92px] w-[75px] h-[34px] items-end'
						// values?.district && 'bg-white'
					),
					maxLength: 4,
				}),
				(values) => ({
					name: 'district',
					maxHeight: 34,
					className: 'text-[16px] text-center',
					containerClassName: cls(
						'absolute top-[95px] left-[188px] w-[46px] h-[34px] items-end'
						// values?.district && 'bg-white'
					),
					maxLength: 10,
				}),
				(values) => ({
					name: 'franchiseName',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[172px] left-[116px] w-[270px] h-[18px]'
					),
					maxLength: 25,
					regex: REGEX.ALLOW_KR_EN,
				}),
				(values) => ({
					name: 'franchiseBusinessNumber',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[172px] left-[390px] w-[290px] h-[18px]'
					),
					maxLength: 20,
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				}),
				(values) => ({
					name: 'franchiseRegistrationNo',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[211px] left-[116px] w-[270px] h-[18px]'
					),
					maxLength: 14,
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				}),
				(values) => ({
					name: 'franchisePhone',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[211px] left-[390px] w-[290px] h-[18px]'
					),
					maxLength: 20,
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				}),
				(values) => ({
					name: 'franchiseAddress',
					maxHeight: 20,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[250px] left-[116px] w-[584px] h-[20px]'
					),
					maxLength: 60,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),

				// 구인 사항 (Recruitment details)
				(values) => ({
					name: 'workAddress',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[298px] left-[116px] w-[582px] h-[18px]'
					),
					maxLength: 60,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values) => ({
					name: 'addressOfResidence',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[337px] left-[116px] w-[582px] h-[18px]'
						// values?.addressOfResidence && 'bg-white'
					),
					maxLength: 60,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				}),
				(values) => ({
					name: 'product',
					maxHeight: 16,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[358px] left-[245px] w-[138px] h-[16px]'
						// values?.product && 'bg-white'
					),
					maxLength: 15,
					regex: REGEX.ALLOW_KR_EN,
				}),
				(values) => ({
					name: 'cultivatedArea',
					maxHeight: 16,
					className: 'text-[12px] text-right',
					containerClassName: cls(
						'absolute top-[382px] left-[178px] w-[92px] h-[16px]'
						// values?.cultivatedArea && 'bg-white'
					),
					maxLength: 10,
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values) => ({
					name: 'cultivatedOther',
					maxHeight: 20,
					className: 'text-[12px] text-left',
					containerClassName: cls(
						'absolute top-[432px] left-[116px] w-[270px] h-[20px]'
					),
					maxLength: 28,
				}),
				(values) => ({
					name: 'jobDescription',
					maxHeight: 20,
					className: 'text-[12px]',
					containerClassName: cls(
						'absolute top-[375px] left-[390px] w-[308px] h-[20px]'
						// values?.jobDescription && 'bg-white'
					),
					maxLength: 40,
					regex: REGEX.ALLOW_KR_EN,
				}),
				(values) => ({
					name: 'requiredManpower',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[397px] left-[179px] w-[39px] h-[18px]'
						// values?.requiredManpower && 'bg-white'
					),
					maxLength: 6,
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values) => ({
					name: 'newCount',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[477px] left-[145px] w-[41px] h-[18px]'
						// values?.newCount && 'bg-white'
					),
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values) => ({
					name: 'rehiredCount',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[477px] left-[261px] w-[26px] h-[18px]'
						// values?.rehiredCount && 'bg-white'
					),
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values) => ({
					name: 'preferredAgeMin',
					maxHeight: 20,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[465px] left-[480px] w-[39px] h-[20px]'
						// values?.preferredAgeMin && 'bg-white'
					),
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values) => ({
					name: 'preferredAgeMax',
					maxHeight: 20,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[465px] left-[570px] w-[39px] h-[20px]'
						// values?.preferredAgeMax && 'bg-white'
					),
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values) => ({
					name: 'preferredNationalityPriority',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[516px] left-[155px] w-[59px] h-[18px]'
						// values?.preferredNationalityPriority && 'bg-white'
					),
					maxLength: 6,
					regex: REGEX.ALLOW_KR_EN,
				}),
				(values) => ({
					name: 'preferredNationalityPrioritySecond',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[516px] left-[265px] w-[65px] h-[18px]'
						// values?.preferredNationalityPrioritySecond && 'bg-white'
					),
					maxLength: 6,
					regex: REGEX.ALLOW_KR_EN,
				}),
				(values) => ({
					name: 'totalEmploymentMonths',
					maxHeight: 16,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[496px] left-[585px] w-[27px] h-[16px]'
						// values?.totalEmploymentMonths && 'bg-white'
					),
					maxLength: 2,
					regex: REGEX.ALLOW_NUMBER,
				}),
				// 월별 계절근로자 소요 인력(명) — 12 monthly cells (Jan..Dec)
				{
					name: 'month1',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[120px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month2',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[167px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month3',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[215px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month4',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[262px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month5',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[309px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month6',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[357px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month7',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[404px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month8',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[451px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month9',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[498px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month10',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[546px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month11',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[593px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'month12',
					maxHeight: 15,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[572px] left-[640px] w-[44px] h-[15px]',
					maxLength: 3,
					regex: REGEX.ALLOW_NUMBER,
				},

				// Footer
				(values) => ({
					name: 'currentYear',
					maxHeight: 16,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[741px] left-[284px] w-[40px] h-[16px] items-end'
						// values?.district && 'bg-white'
					),
					maxLength: 4,
				}),
				(values) => ({
					name: 'currentRegion',
					maxHeight: 16,
					className: 'text-[12px] text-center',
					containerClassName: cls(
						'absolute top-[739px] left-[350px] w-[42px] h-[16px]'
						// values?.currentRegion && 'bg-white'
					),
					maxLength: 10,
				}),
				(values) => ({
					name: 'registrantName',
					maxHeight: 32,
					className: 'text-[12px] text-left',
					containerClassName: cls(
						'absolute top-[793px] left-[385px] w-[150px] h-[32px] items-start'
						// values?.registrantName && 'bg-white'
					),
					maxLength: 20,
					regex: REGEX.ALLOW_KR_EN,
				}),
				(values) => ({
					name: 'districtName',
					maxHeight: 32,
					className: 'text-[12px] text-right',
					containerClassName: cls(
						'absolute top-[814px] left-[103px] w-[60px] h-[32px] items-end'
						// values?.districtName && 'bg-white'
					),
					maxLength: 20,
					regex: REGEX.ALLOW_KR_EN,
				}),
			],
			checkboxes: [
				// 희망 도입 방식 — single select 'O' into one of the four [ ]
				{
					name: 'preferredJoiningMethod',
					classNames: [
						'absolute top-[417px] left-[395px] w-4 h-4',
						'absolute top-[417px] left-[490px] w-4 h-4',
						'absolute top-[438px] left-[395px] w-4 h-4',
						'absolute top-[438px] left-[552px] w-4 h-4',
					],
					iconType: 'circle',
					options: preferredJoiningMethodOption,
				},
			],
			datePickers: [
				{
					name: 'employmentStartDate',
					segmentedInputs: true,
					format: 'YYYY          MM        DD',
					containerClassName: 'absolute top-[524px] left-[395px]',
					style: { fontSize: '13px' },
					boxStyle: { marginTop: '-10px' },
				},
				{
					name: 'employmentEndDate',
					segmentedInputs: true,
					format: 'YYYY          MM        DD',
					containerClassName: 'absolute top-[524px] left-[553px]',
					style: { fontSize: '13px' },
					boxStyle: { marginTop: '-10px' },
				},
				{
					name: 'formSubmissionDate',
					segmentedInputs: true,
					format: 'YYYY            MM            DD',
					containerClassName: 'absolute top-[775px] left-[525px]',
					style: { fontSize: '13px' },
					boxStyle: { marginTop: '-10px' },
				},
			],
			signatures: [
				{
					name: 'registrantSignature',
					className: 'absolute top-[800px] left-[582px] w-[110px] h-[50px]',
				},
			],
			highlightBoxes: [
				// Title: 년 / 시·군·구
				{
					name: 'registryDate',
					className: 'top-[91px] left-[92px] !h-[34px] w-[75px]',
				},
				{
					name: 'district',
					className: 'top-[91px] left-[188px] !h-[34px] w-[46px]',
				},

				// 신청자 현황 (Applicant status)
				{
					name: 'franchiseName',
					className: 'top-[153px] left-[116px] !h-[18px] w-[76px]',
				},
				{
					name: 'franchiseBusinessNumber',
					className: 'top-[153px] left-[390px] !h-[18px] w-[195px]',
				},
				{
					name: 'franchiseRegistrationNo',
					className: 'top-[192px] left-[116px] !h-[18px] w-[188px]',
				},
				{
					name: 'franchisePhone',
					className: 'top-[192px] left-[390px] !h-[18px] w-[57px]',
				},
				{
					name: 'franchiseAddress',
					className: 'top-[232px] left-[116px] !h-[18px] w-[30px]',
				},

				// 구인 사항 (Recruitment details)
				{
					name: 'workAddress',
					className: 'top-[280px] left-[116px] !h-[18px] w-[143px]',
				},
				{
					name: 'addressOfResidence',
					className: 'top-[319px] left-[116px] !h-[18px] w-[215px]',
				},
				{
					name: 'product',
					className: 'top-[358px] left-[115px] !h-[16px] w-[126px]',
				},
				{
					name: 'cultivatedArea',
					className: 'top-[378px] left-[115px] !h-[16px] w-[57px]',
				},
				{
					name: 'jobDescription',
					className: 'top-[358px] left-[390px] !h-[16px] w-[54px]',
				},
				{
					name: 'requiredManpower',
					className: 'top-[397px] left-[115px] !h-[18px] w-[63px]',
				},
				{
					name: 'preferredJoiningMethod',
					className: 'top-[397px] left-[390px] !h-[18px] w-[92px]',
				},
				{
					name: 'cultivatedOther',
					className: 'top-[432px] left-[115px] !h-[20px] w-[270px]',
				},
				{
					name: 'newCount',
					className: 'top-[476px] left-[145px] !h-[18px] w-[41px]',
				},
				{
					name: 'rehiredCount',
					className: 'top-[476px] left-[261px] !h-[18px] w-[26px]',
				},

				{
					name: 'preferredAgeMin',
					className: 'top-[465px] left-[480px] !h-[20px] w-[39px]',
				},
				{
					name: 'preferredAgeMax',
					className: 'top-[465px] left-[570px] !h-[20px] w-[39px]',
				},

				{
					name: 'preferredNationalityPriority',
					className: 'top-[515px] left-[155px] !h-[18px] w-[59px]',
				},
				{
					name: 'preferredNationalityPrioritySecond',
					className: 'top-[515px] left-[265px] !h-[18px] w-[65px]',
				},

				// { name: 'expectedEmploymentPeriod', className: 'top-[536px] left-[390px] !h-[16px] w-[95px]' },
				{
					name: 'totalEmploymentMonths',
					className: 'top-[496px] left-[585px] !h-[16px] w-[27px]',
				},

				{
					name: 'employmentStartDate',
					className: 'top-[517px] left-[395px] !h-[16px] w-[145px]',
				},
				{
					name: 'employmentEndDate',
					className: 'top-[517px] left-[552px] !h-[16px] w-[145px]',
				},
				{
					name: 'month1',
					className: 'top-[572px] left-[120px] !h-[15px] w-[44px]',
				},
				{
					name: 'month2',
					className: 'top-[572px] left-[167px] !h-[15px] w-[44px]',
				},
				{
					name: 'month3',
					className: 'top-[572px] left-[215px] !h-[15px] w-[44px]',
				},
				{
					name: 'month4',
					className: 'top-[572px] left-[262px] !h-[15px] w-[44px]',
				},
				{
					name: 'month5',
					className: 'top-[572px] left-[309px] !h-[15px] w-[44px]',
				},
				{
					name: 'month6',
					className: 'top-[572px] left-[357px] !h-[15px] w-[44px]',
				},
				{
					name: 'month7',
					className: 'top-[572px] left-[404px] !h-[15px] w-[44px]',
				},
				{
					name: 'month8',
					className: 'top-[572px] left-[451px] !h-[15px] w-[44px]',
				},
				{
					name: 'month9',
					className: 'top-[572px] left-[498px] !h-[15px] w-[44px]',
				},
				{
					name: 'month10',
					className: 'top-[572px] left-[546px] !h-[15px] w-[44px]',
				},
				{
					name: 'month11',
					className: 'top-[572px] left-[593px] !h-[15px] w-[44px]',
				},
				{
					name: 'month12',
					className: 'top-[572px] left-[640px] !h-[15px] w-[44px]',
				},

				// Footer: 위와 같이 … / 년 월 일 / 신청인 / 서명 또는 인
				{
					name: 'currentYear',
					className: 'top-[737px] left-[284px] !h-[16px] w-[42px]',
				},
				{
					name: 'currentRegion',
					className: 'top-[737px] left-[350px] !h-[16px] w-[42px]',
				},
				{
					name: 'formSubmissionDate',
					className: 'top-[767px] left-[525px] !h-[16px] w-[162px]',
				},
				{
					name: 'districtName',
					className: 'top-[827px] left-[103px] !h-[17px] w-[60px]',
				},
				{
					name: 'registrantName',
					className: 'top-[793px] left-[339px] !h-[17px] w-[45px]',
				},
				{
					name: 'registrantSignature',
					className: 'top-[800px] left-[582px] !h-[17px] w-[110px]',
				},
			],
		},
		{
			background: {
				src: Images.formForRecruitingForeignWorkers2,
				className: 'template',
				dataSource: Images.formForRecruitingForeignWorkers2,
			},
			// 1-1. 근로시간 (working start/end time) — TimePicker
			timePickers: [
				{
					name: 'workingStartTime',
					className: 'w-[73px] [&>.ant-picker-input>input]:!text-[12px]',
					format: 'HH        mm',
					placeholder: '',
					containerClassName: 'absolute top-[185px] left-[309px]',
					style: { fontSize: '12px' },
				},
				{
					name: 'workingEndTime',
					className: 'w-[73px] [&>.ant-picker-input>input]:!text-[12px]',
					format: 'HH        mm',
					placeholder: '',
					containerClassName: 'absolute top-[185px] left-[418px]',
					style: { fontSize: '12px' },
				},
			],
			textAreas: [
				// 1-1. 근로시간
				{
					name: 'workingStartDay',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[167px] left-[287px] w-[28px] h-[18px]',
					maxLength: 3,
				},
				{
					name: 'workingEndDay',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[167px] left-[371px] w-[25px] h-[18px]',
					maxLength: 3,
				},
				{
					name: 'totalMonthlyWorkingHours',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[187px] left-[587px] w-[50px] h-[18px]',
					maxLength: 4,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'flexibleHoursLimit',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[210px] left-[515px] w-[50px] h-[18px]',
					maxLength: 4,
					regex: REGEX.ALLOW_NUMBER,
				},

				// 1-2. 휴게시간
				{
					name: 'breakCountPerDay',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[227px] left-[228px] w-[28px] h-[18px]',
					maxLength: 2,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'breakHours',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[227px] left-[301px] w-[28px] h-[18px]',
					maxLength: 2,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'breakMinutes',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[227px] left-[381px] w-[28px] h-[18px]',
					maxLength: 2,
					regex: REGEX.ALLOW_NUMBER,
				},

				// 1-3. 휴일 (기타)
				{
					name: 'restDaysOther',
					maxHeight: 18,
					className: 'text-[12px]',
					containerClassName:
						'absolute top-[287px] left-[307px] w-[176px] h-[18px]',
					maxLength: 18,
				},

				// 1-4. 임금 및 지급방법
				{
					name: 'wageAmount',
					maxHeight: 18,
					className: 'text-[12px] text-right',
					containerClassName:
						'absolute top-[329px] left-[303px] w-[73px] h-[18px]',
					maxLength: 10,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				},
				(data, index) => ({
					name: 'bonusAmount',
					maxHeight: 18,
					className: 'text-[12px] text-right',
					containerClassName:
						'absolute top-[349px] left-[364px] w-[54px] h-[18px]',
					maxLength: 7,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: data?.isBonusPaid === '1',
					currency: true,
				}),
				(data, index) => ({
					name: 'allowanceAmount',
					maxHeight: 18,
					className: 'text-[12px] text-right',
					containerClassName:
						'absolute top-[349px] left-[498px] w-[47px] h-[18px]',
					maxLength: 6,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: data?.isBonusPaid === '1',
					currency: true,
				}),
				{
					name: 'overtimeHourlyWage',
					maxHeight: 18,
					className: 'text-[12px] text-right',
					containerClassName:
						'absolute top-[391px] left-[269px] w-[88px] h-[18px]',
					maxLength: 12,
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
					currency: true,
				},
				{
					name: 'salaryPeriodStartDate',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[411px] left-[268px] w-[42px] h-[18px]',
					maxLength: 4,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'salaryPeriodEndDate',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[411px] left-[361px] w-[42px] h-[18px]',
					maxLength: 4,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'paymentDayOfMonth',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[433px] left-[268px] w-[42px] h-[18px]',
					maxLength: 4,
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'paymentDayOfWeek',
					maxHeight: 18,
					className: 'text-[12px] text-center',
					containerClassName:
						'absolute top-[433px] left-[569px] w-[42px] h-[18px]',
					maxLength: 5,
				},

				// 1-5. 숙식 제공 (fees)
				(data, index) => ({
					name: 'accommodationWorkerFeeAmount',
					maxHeight: 18,
					className: 'text-[12px] text-right',
					containerClassName: cn(
						'absolute top-[517px] left-[462px] w-[74px] h-[18px] invisible',
						data?.isAccommodationPaidByWorker === '0' && 'visible'
					),
					maxLength: 10,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					disabled: data?.isAccommodationPaidByWorker === '1',
					currency: true,
				}),
				(data, index) => ({
					name: 'mealsWorkerFeeAmount',
					maxHeight: 18,
					className: 'text-[12px] text-right',
					containerClassName: cn(
						'absolute top-[542px] left-[576px] w-[74px] h-[18px] invisible',
						data?.isMealsProvided === '0' && 'visible'
					),
					maxLength: 10,
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
					currency: true,
				}),

				// 1-6. 기타
				{
					name: 'other',
					maxHeight: 31,
					className: 'text-[12px]',
					containerClassName:
						'absolute top-[598px] left-[191px] w-[509px] h-[31px]',
					maxLength: 100,
				},

				// 재고용 신청 외국인 근로자 인적사항 (reemployed worker info)
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'priorityOrder',
					tabIndex: index * 8 + 1,
					maxHeight: 24,
					className: 'text-[11px] text-center',
					containerClassName: 'absolute left-[67px] w-[30px] h-[24px]',
					maxLength: 3,
					'data-tabIndex': index,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'fullName',
					tabIndex: index * 8 + 2,
					maxHeight: 24,
					className: 'text-[11px] text-center',
					containerClassName: 'absolute left-[104px] w-[112px] h-[24px]',
					maxLength: 13,
					regex: REGEX.ALLOW_KR_EN,
					'data-tabIndex': index,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'nationality',
					tabIndex: index * 8 + 3,
					maxHeight: 24,
					className: 'text-[11px] text-center',
					containerClassName:
						'absolute top-[855px] left-[222px] w-[70px] h-[24px]',
					maxLength: 8,
					regex: REGEX.ALLOW_KR_EN,
					'data-tabIndex': index,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'alienRegistrationNumber',
					tabIndex: index * 8 + 4,
					maxHeight: 24,
					className: 'text-[11px] text-center',
					containerClassName:
						'absolute top-[855px] left-[298px] w-[87px] h-[24px]',
					maxLength: 12,
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
					'data-tabIndex': index,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'passportNumber',
					tabIndex: index * 8 + 5,
					maxHeight: 24,
					className: 'text-[11px] text-center uppercase',
					containerClassName:
						'absolute top-[855px] left-[389px] w-[87px] h-[24px]',
					maxLength: 12,
					regex: REGEX.ALLOW_EN_NUMBER_DASH,
					'data-tabIndex': index,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'totalContractDays',
					tabIndex: index * 8 + 6,
					maxHeight: 24,
					className: 'text-[11px] text-center',
					containerClassName:
						'absolute top-[855px] left-[480px] w-[53px] h-[24px]',
					maxLength: 4,
					regex: REGEX.ALLOW_NUMBER,
					'data-tabIndex': index,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'actualWorkingDays',
					tabIndex: index * 8 + 7,
					maxHeight: 24,
					className: 'text-[11px] text-center',
					containerClassName:
						'absolute top-[855px] left-[550px] w-[53px] h-[24px]',
					maxLength: 4,
					'data-tabIndex': index,
					regex: REGEX.ALLOW_NUMBER,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				// Column 8 — auto-computed from 실제/총 근로일수 in onChangeInputJson,
				// but still manually editable (user can override).
				(values, index) => ({
					jsonKey: 'foreignWorkersRegisteringFormJson',
					repeat: 5,
					name: 'is75PercentContractFulfilled',
					tabIndex: index * 8 + 8,
					maxHeight: 24,
					className: 'text-[16px] text-center uppercase',
					containerClassName: cn(
						'absolute top-[855px] left-[625px] w-[70px] h-[24px]',
						values?.foreignWorkersRegisteringFormJson?.[index]
							?.is75PercentContractFulfilled && 'bg-white'
					),
					'data-tabIndex': index,
					maxLength: 2,
					boxStyle: {
						top: `${855 + 29 * index}px`,
					},
				}),
				{
					name: 'remarks',
					maxHeight: 20,
					className: 'text-[12px]',
					containerClassName:
						'absolute top-[1010px] left-[66px] w-[630px] h-[20px]',
					maxLength: 65,
				},
			],
			checkboxes: [
				// 1-3. 휴일
				{
					name: 'weeklyRestDays',
					iconType: 'circle',
					options: weeklyRestDaysOption,
					classNames: [
						'absolute top-[266px] left-[191px] w-4 h-4',
						'absolute top-[266px] left-[271px] w-4 h-4',
						'absolute top-[287px] left-[191px] w-4 h-4',
					],
					getResetKey(currentSelectKey) {
						return ['restDaysOther'];
					},
				},
				// 1-4. 임금 지급방법
				{
					name: 'wageAndPaymentMethod',
					iconType: 'circle',
					options: paymentTypeOption,
					classNames: [
						'absolute top-[327px] left-[398px] w-4 h-4',
						'absolute top-[327px] left-[464px] w-4 h-4',
						'absolute top-[327px] left-[544px] w-4 h-4',
						'absolute top-[327px] left-[618px] w-4 h-4',
					],
				},
				{
					name: 'isBonusPaid',
					iconType: 'circle',
					options: bonusAndAllowancesOption,
					classNames: [
						'absolute top-[349px] left-[274px] w-4 h-4',
						'absolute top-[349px] left-[635px] w-4 h-4',
					],
					getResetKey(currentSelectKey) {
						return ['bonusAmount', 'allowanceAmount'];
					},
				},
				{
					name: 'isPaidInCurrentMonth',
					iconType: 'circle',
					options: bonusAndAllowancesOption,
					classNames: [
						'absolute top-[411px] left-[517px] w-4 h-4',
						'absolute top-[411px] left-[603px] w-4 h-4',
					],
				},
				{
					name: 'isPaidInCurrentWeek',
					iconType: 'circle',
					options: bonusAndAllowancesOption,
					classNames: [
						'absolute top-[432px] left-[374px] w-4 h-4',
						'absolute top-[432px] left-[462px] w-4 h-4',
					],
				},
				// 1-5. 숙식 제공
				{
					name: 'isAccommodationProvided',
					iconType: 'circle',
					options: ['0'],
					classNames: ['absolute top-[497px] left-[197px] w-4 h-4'],
				},
				{
					name: 'accommodationType',
					iconType: 'circle',
					options: ['0', '1', '2', '3', '4'],
					classNames: [
						'absolute top-[470px] left-[331px] w-4 h-4',
						'absolute top-[470px] left-[390px] w-4 h-4',
						'absolute top-[491px] left-[331px] w-4 h-4',
						'absolute top-[491px] left-[438px] w-4 h-4',
						'absolute top-[491px] left-[544px] w-4 h-4',
					],
				},
				{
					name: 'isAccommodationPaidByWorker',
					iconType: 'circle',
					options: bonusAndAllowancesOption,
					classNames: [
						'absolute top-[516px] left-[331px] w-4 h-4',
						'absolute top-[516px] left-[571px] w-4 h-4',
					],
					getResetKey(currentSelectKey) {
						return ['accommodationWorkerFeeAmount'];
					},
				},
				{
					name: 'isMealsProvided',
					iconType: 'circle',
					options: bonusAndAllowancesOption,
					classNames: [
						'absolute top-[551px] left-[197px] w-4 h-4',
						'absolute top-[579px] left-[193px] w-4 h-4',
					],
					getResetKey(currentSelectKey) {
						return [
							'individualMeals',
							'mealsWorkerFeeAmount',
							'isMealsFreeForWorker',
						];
					},
				},
				(data, index) => ({
					name: 'individualMeals',
					iconType: 'circle',
					type: CheckboxType.MULTIPLE,
					options: individualMealsOption,
					classNames: [
						'absolute top-[540px] left-[269px] w-4 h-4',
						cn(
							'absolute top-[540px] left-[335px] w-4 h-4 invisible',
							data?.isMealsProvided === '0' && 'visible'
						),
						'absolute top-[540px] left-[402px] w-4 h-4',
					],
					// getResetKey(currentSelectKey) {
					// 	return ['isMealsFreeForWorker'];
					// },
				}),
				(data, index) => ({
					name: 'isMealsFreeForWorker',
					iconType: 'circle',
					options: ['0'],
					classNames: [
						cn(
							'absolute top-[561px] left-[269px] w-4 h-4 invisible',
							data?.isMealsProvided === '0' && 'visible'
						),
					],
					// getResetKey(currentSelectKey) {
					// 	return ['individualMeals', 'mealsWorkerFeeAmount'];
					// },
				}),
				// 고용주 사실관계 기재란
				{
					name: 'employerFactCheckSection',
					iconType: 'circle',
					options: preferredJoiningMethodOption,
					classNames: [
						'absolute top-[687px] left-[243px] w-4 h-4',
						'absolute top-[706px] left-[243px] w-4 h-4',
						'absolute top-[725px] left-[243px] w-4 h-4',
						'absolute top-[745px] left-[243px] w-4 h-4',
					],
					type: CheckboxType.MULTIPLE,
				},
				{
					name: 'cooperateDataSubmission',
					iconType: 'circle',
					options: bonusAndAllowancesOption,
					classNames: [
						'absolute top-[765px] left-[585px] w-4 h-4',
						'absolute top-[765px] left-[664px] w-4 h-4',
					],
				},
			],
			highlightBoxes: [
				{
					name: 'workingStartDay',
					className: 'top-[187px] left-[110px] !h-[18px] w-[75px]',
				},
				{
					name: 'workingStartDay',
					className: 'top-[167px] left-[287px] !h-[18px] w-[75px]',
				},
				{
					name: 'workingEndDay',
					className: 'top-[167px] left-[371px] !h-[18px] w-[75px]',
				},
				{
					name: 'workingStartTime',
					className: 'top-[187px] left-[287px] !h-[18px] w-[95px]',
				},
				{
					name: 'workingEndTime',
					className: 'top-[187px] left-[411px] !h-[18px] w-[95px]',
				},
				{
					name: 'totalMonthlyWorkingHours',
					className: 'top-[187px] left-[587px] !h-[18px] w-[50px]',
				},
				{
					name: 'flexibleHoursLimit',
					className: 'top-[208px] left-[515px] !h-[18px] w-[50px]',
				},
				{
					name: 'breakCountPerDay',
					className: 'top-[236px] left-[110px] !h-[18px] w-[75px]',
				},
				{
					name: 'breakCountPerDay',
					className: 'top-[227px] left-[228px] !h-[18px] w-[28px]',
				},
				{
					name: 'breakHours',
					className: 'top-[227px] left-[301px] !h-[18px] w-[28px]',
				},
				{
					name: 'breakMinutes',
					className: 'top-[227px] left-[381px] !h-[18px] w-[28px]',
				},

				{
					name: 'weeklyRestDays',
					className: 'top-[287px] left-[110px] !h-[18px] w-[75px]',
				},
				// {
				// 	name: 'weeklyRestDays',
				// 	className: 'top-[266px] left-[190px] !h-[18px] w-[19px]',
				// },
				// {
				// 	name: 'weeklyRestDays',
				// 	className: 'top-[266px] left-[270px] !h-[18px] w-[19px]',
				// },
				// {
				// 	name: 'weeklyRestDays',
				// 	className: 'top-[287px] left-[190px] !h-[18px] w-[19px]',
				// },
				{
					name: 'restDaysOther',
					className: 'top-[287px] left-[307px] !h-[18px] w-[176px]',
				},

				{
					name: 'wageAmount',
					className: 'top-[379px] left-[110px] !h-[40px] w-[75px]',
				},
				{
					name: 'wageAmount',
					className: 'top-[327px] left-[303px] !h-[18px] w-[73px]',
				},
				{
					name: 'wageAndPaymentMethod',
					className: 'top-[327px] left-[397px] !h-[18px] w-[20px]',
				},
				{
					name: 'wageAndPaymentMethod',
					className: 'top-[327px] left-[463px] !h-[18px] w-[20px]',
				},
				{
					name: 'wageAndPaymentMethod',
					className: 'top-[327px] left-[543px] !h-[18px] w-[20px]',
				},
				{
					name: 'wageAndPaymentMethod',
					className: 'top-[327px] left-[617px] !h-[18px] w-[20px]',
				},

				{
					name: 'isBonusPaid',
					className: 'top-[347px] left-[272px] !h-[18px] w-[20px]',
				},
				{
					name: 'isBonusPaid',
					className: 'top-[347px] left-[633px] !h-[18px] w-[20px]',
				},

				{
					name: 'bonusAmount',
					className: 'top-[347px] left-[364px] !h-[18px] w-[54px]',
				},
				{
					name: 'allowanceAmount',
					className: 'top-[347px] left-[498px] !h-[18px] w-[47px]',
				},
				{
					name: 'overtimeHourlyWage',
					className: 'top-[389px] left-[269px] !h-[18px] w-[88px]',
				},

				{
					name: 'salaryPeriodStartDate',
					className: 'top-[411px] left-[268px] !h-[18px] w-[42px]',
				},
				{
					name: 'salaryPeriodEndDate',
					className: 'top-[411px] left-[361px] !h-[18px] w-[42px]',
				},
				{
					name: 'isPaidInCurrentMonth',
					className: 'top-[411px] left-[516px] !h-[18px] w-[20px]',
				},
				{
					name: 'isPaidInCurrentMonth',
					className: 'top-[411px] left-[602px] !h-[18px] w-[20px]',
				},
				{
					name: 'paymentDayOfMonth',
					className: 'top-[431px] left-[268px] !h-[18px] w-[42px]',
				},
				{
					name: 'isPaidInCurrentWeek',
					className: 'top-[431px] left-[373px] !h-[18px] w-[20px]',
				},
				{
					name: 'isPaidInCurrentWeek',
					className: 'top-[431px] left-[461px] !h-[18px] w-[20px]',
				},
				{
					name: 'paymentDayOfWeek',
					className: 'top-[431px] left-[569px] !h-[18px] w-[42px]',
				},

				{
					name: 'isAccommodationProvided',
					className: 'top-[515px] left-[110px] !h-[54px] w-[30px]',
				},
				{
					name: 'accommodationType',
					className: 'top-[492px] left-[150px] !h-[23px] w-[30px]',
				},
				{
					name: 'accommodationType',
					className: 'top-[555px] left-[150px] !h-[23px] w-[30px]',
				},
				// {
				// 	name: 'isAccommodationProvided',
				// 	className: 'top-[496px] left-[196px] !h-[18px] w-[20px]',
				// },
				{
					name: 'accommodationType',
					className: 'top-[469px] left-[330px] !h-[18px] w-[20px]',
				},
				{
					name: 'accommodationType',
					className: 'top-[469px] left-[389px] !h-[18px] w-[20px]',
				},
				{
					name: 'accommodationType',
					className: 'top-[490px] left-[330px] !h-[18px] w-[20px]',
				},
				{
					name: 'accommodationType',
					className: 'top-[490px] left-[436px] !h-[18px] w-[20px]',
				},
				{
					name: 'accommodationType',
					className: 'top-[490px] left-[542px] !h-[18px] w-[20px]',
				},
				{
					name: 'isAccommodationPaidByWorker',
					className: 'top-[515px] left-[330px] !h-[18px] w-[20px]',
				},
				{
					name: 'isAccommodationPaidByWorker',
					className: 'top-[515px] left-[569px] !h-[18px] w-[20px]',
				},
				{
					name: 'accommodationWorkerFeeAmount',
					className: 'top-[515px] left-[462px] !h-[18px] w-[74px]',
				},

				// {
				// 	name: 'isMealsProvided',
				// 	className: 'top-[550px] left-[196px] !h-[18px] w-[20px]',
				// },
				{
					name: 'individualMeals',
					className: 'top-[540px] left-[267px] !h-[18px] w-[20px]',
				},
				{
					name: 'individualMeals',
					className: 'top-[540px] left-[333px] !h-[18px] w-[20px]',
				},
				{
					name: 'individualMeals',
					className: 'top-[540px] left-[400px] !h-[18px] w-[20px]',
				},
				{
					name: 'mealsWorkerFeeAmount',
					className: 'top-[540px] left-[576px] !h-[18px] w-[74px]',
				},
				{
					name: 'isMealsFreeForWorker',
					className: 'top-[560px] left-[267px] !h-[18px] w-[20px]',
				},
				// {
				// 	name: 'isMealsProvided',
				// 	className: 'top-[578px] left-[191px] !h-[18px] w-[20px]',
				// },
				{
					name: 'other',
					className: 'top-[598px] left-[110px] !h-[31px] w-[45px]',
				},
				{
					name: 'other',
					className: 'top-[598px] left-[191px] !h-[31px] w-[509px]',
				},

				{
					name: 'employerFactCheckSection',
					className: 'top-[698px] left-[104px] !h-[72px] w-[74px]',
				},
				// {
				// 	name: 'employerFactCheckSection',
				// 	className: 'top-[706px] left-[241px] !h-[18px] w-[20px]',
				// },
				// {
				// 	name: 'employerFactCheckSection',
				// 	className: 'top-[725px] left-[241px] !h-[18px] w-[20px]',
				// },
				// {
				// 	name: 'employerFactCheckSection',
				// 	className: 'top-[745px] left-[241px] !h-[18px] w-[20px]',
				// },
				{
					name: 'cooperateDataSubmission',
					className: 'top-[764px] left-[583px] !h-[18px] w-[20px]',
				},
				{
					name: 'cooperateDataSubmission',
					className: 'top-[764px] left-[662px] !h-[18px] w-[20px]',
				},

				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[815px] left-[67px] !h-[30px] w-[30px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[820px] left-[145px] !h-[20px] w-[30px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[820px] left-[242px] !h-[20px] w-[30px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[815px] left-[315px] !h-[30px] w-[55px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[820px] left-[405px] !h-[20px] w-[55px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[808px] left-[487px] !h-[44px] w-[55px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[815px] left-[555px] !h-[30px] w-[55px]',
				},
				{
					name: 'foreignWorkersRegisteringFormJson',
					className: 'top-[808px] left-[625px] !h-[44px] w-[70px]',
				},
				{
					name: 'remarks',
					className: 'top-[996px] left-[66px] !h-[16px] w-[156px]',
				},
			],
		},
	],
};
